import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import { notificationsApi } from '../../api/notifications';

const Header = ({ collapsed, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  // Fetch unread notifications count every 30 seconds
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await notificationsApi.getUnreadCount();
      return response;
    },
    enabled: isAuthenticated,
    refetchInterval: 30000, // Poll every 30 seconds
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const unreadCount = unreadCountData?.unreadCount || unreadCountData?.count || unreadCountData?.unread_count || 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrador',
      despachador: 'Despachador',
      visor_reportes: 'Visor de Reportes'
    };
    return labels[role] || role;
  };

  return (
    <header className="header" style={{
      height: '60px',
      background: '#171717',
      borderBottom: '1px solid rgba(255,255,255,.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      position: 'sticky',
      top: 0,
      zIndex: 20
    }}>
      <div className="header__left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {collapsed && (
          <img src="/racingmotors.png" alt="Racing Motors" className="header__logo" style={{ height: '28px', objectFit: 'contain', opacity: 0.9 }} />
        )}
        <h1 className="header__title" style={{ fontSize: '18px', fontWeight: '600', color: '#eaeaea', margin: 0 }}>Racing Motors</h1>
      </div>

      <div className="header__right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Notifications Icon */}
        <button
          ref={notificationRef}
          onClick={() => {
            navigate('/notifications');
          }}
          style={{
            background: 'rgba(255,255,255,.05)',
            border: '1px solid rgba(255,255,255,.1)',
            color: '#eaeaea',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            borderRadius: '8px',
            transition: 'all 0.15s',
            position: 'relative',
            width: '40px',
            height: '40px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,.1)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,.05)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)';
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>notifications</span>
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: '#ef4444',
              color: '#fff',
              borderRadius: '10px',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: '600',
              padding: '0 4px',
              border: '2px solid #171717'
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User Menu */}
        <div className="header__user" ref={dropdownRef} style={{ position: 'relative' }}>
          <button 
            className="header__userbtn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              background: 'rgba(255,255,255,.05)',
              border: '1px solid rgba(255,255,255,.1)',
              color: '#eaeaea',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'all 0.15s',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,.1)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,.05)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)';
            }}
          >
            <span className="header__username">{user?.nombre || user?.email}</span>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>expand_more</span>
          </button>

          {showUserMenu && (
            <div className="header__menu" style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              background: '#2c2c2c',
              borderRadius: '8px',
              padding: '8px 0',
              minWidth: '200px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              zIndex: 100
            }}>
              <div className="header__userinfo" style={{ padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="header__name" style={{ fontSize: '14px', fontWeight: '600', color: '#eaeaea', marginBottom: '2px' }}>{user?.nombre || 'Usuario'}</div>
                <div className="header__email" style={{ fontSize: '12px', color: '#a0a0a0', marginBottom: '2px' }}>{user?.email}</div>
                <div className="header__role" style={{ fontSize: '12px', color: '#8BAD9D', fontWeight: '500' }}>{getRoleLabel(user?.role)}</div>
              </div>
              <button 
                onClick={onLogout} 
                className="header__logout"
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  color: '#eaeaea',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,.08)'}
                onMouseLeave={(e) => e.target.style.background = 'transparent'}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>logout</span>
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;